export interface PddDailyRecord {
  date: string
  spend: number | null
  attributedGmv: number | null
  attributedRoi: number | null
  netGmv: number | null
  netRoi: number | null
  netOrders: number | null
  settledGmv: number | null
  settledRoi: number | null
  settledOrders: number | null
  orders: number | null
  impressions: number | null
  clicks: number | null
  dataState: Record<string, 'reported' | 'missing' | 'invalid'>
  unavailableRawValues?: Record<string, string>
}

export interface PddProductRecord {
  productId: string
  name: string
  group: string
  promotionName: string
  bidding: string
  spend: number | null
  attributedGmv: number | null
  attributedRoi: number | null
  netGmv: number | null
  netRoi: number | null
  netOrders: number | null
  settledGmv: number | null
  settledRoi: number | null
  settledOrders: number | null
  orders: number | null
  impressions: number | null
  clicks: number | null
  dataState: Record<string, 'reported' | 'missing' | 'invalid'>
  unavailableRawValues?: Record<string, string>
}

export interface PddMarketingOpportunity {
  productId: string
  name: string
  sales30d: number
  benefit: string
  currentPrice: string
  campaignPrice: string
}

export type PddOrderOutcome = 'confirmed' | 'after_sales_pending' | 'refunded' | 'cancelled' | 'pending_payment' | 'paid_unresolved'

export interface PddOrderMetrics {
  orderCount: number
  soldItemCount: number
  productGross: number
  merchantDiscount: number
  platformDiscount: number
  userPaid: number
  merchantReceivable: number
}

export interface PddDailyOrderRecord extends PddOrderMetrics {
  date: string
  outcome: PddOrderOutcome
}

export const pinduoduoIsPaidOrderOutcome = (outcome: PddOrderOutcome) =>
  outcome === 'confirmed' || outcome === 'after_sales_pending' || outcome === 'refunded' || outcome === 'paid_unresolved'

export const pinduoduoPaidOrderItemCount = (
  rows: readonly Pick<PddDailyOrderRecord, 'outcome' | 'soldItemCount'>[],
) => rows.reduce((sum, row) => sum + (pinduoduoIsPaidOrderOutcome(row.outcome) ? row.soldItemCount : 0), 0)

export const pinduoduoPaidOrderAmount = (
  rows: readonly Pick<PddDailyOrderRecord, 'outcome' | 'merchantReceivable'>[],
) => rows.reduce((sum, row) => sum + (pinduoduoIsPaidOrderOutcome(row.outcome) ? row.merchantReceivable : 0), 0)

export const pinduoduoReplenishmentQuantity = (
  recent7SoldItemCount: number,
  erpAvailable: number,
) => Math.max(recent7SoldItemCount - erpAvailable, 0)

export interface PddDailySkuOrderRecord extends PddDailyOrderRecord {
  productId: string
  skuCode: string
  productName: string
  variantAttributes: string
}

export interface PinduoduoOrderFacts {
  schemaVersion: string
  generatedAt: string
  meta: {
    platformId: 'pinduoduo'
    storeId: string
    storeName: string
    timezone: string
    queryStart: string
    queryEnd: string
    exportedAt: string
    sourceFileName: string
    sourceSha256: string
    sourceRows: number
    sourceColumns: number
    sourceRetained: false
    privacy: string
    droppedSensitiveColumns: string[]
    reconciliation?: {
      state: 'incomplete' | 'ready'
      reconstructedPaymentStart?: string
      observationEnd?: string
      unresolvedPaidOrderCount: number
      unresolvedPaidItemCount: number
      unmatchedAfterSalesOrderCount: number
      sourceManifestSha256: string
    }
    outcomeSources?: Partial<Record<PddOrderOutcome, { queryStart: string; queryEnd: string }>>
    caliber: string
  }
  summary: Record<Exclude<PddOrderOutcome, 'paid_unresolved'>, PddOrderMetrics> & Partial<Record<'paid_unresolved', PddOrderMetrics>>
  daily: PddDailyOrderRecord[]
  dailySku: PddDailySkuOrderRecord[]
}

export interface PinduoduoData {
  schemaVersion: string
  generatedAt: string
  meta: {
    platform: 'pinduoduo'
    platformId: 'pinduoduo'
    platformName: string
    storeId: string
    platformStoreId: string | null
    platformStoreIdState: 'reported' | 'not_acquired'
    storeName: string
    currency: string
    timezone: string
    businessDayCutoff: string
    capabilities: Record<string, { state: string; dataGrain: string[]; note: string }>
    queryStart: string
    queryEnd: string
    collectedAt: string
    promotionLastActiveDate: string | null
    pageContractCount: number
    sourceIdentity: {
      state: 'native' | 'legacy_sha256_binding'
      storeId: string
      manifestSha256?: string
      aggregateSnapshotSha256?: string
      boundAt?: string
      pageContractCount: number
    }
    promotionReportsReconciled: boolean
    sourceRun: string
    caliber: string
  }
  summary: {
    spend: number | null
    attributedGmv: number | null
    netGmv: number | null
    settledGmv: number | null
    orders: number | null
    netOrders: number | null
    settledOrders: number | null
    impressions: number | null
    clicks: number | null
    attributedRoi: number | null
    netRoi: number | null
    settledRoi: number | null
    dataState: Record<string, 'reported' | 'partial' | 'missing' | 'invalid' | 'derived' | 'not_applicable'>
  }
  catalog: { listedProductCount: number; realtimeProductCount: number; promotionCount: number }
  quality: {
    pageUpdatedAt: string
    ratingRankPercentile: number
    negativeFeedbackCount30d: number
    badReviewCount30d: number
    badReviewRate30d: number
    qualityWarningCount: number
    qualityViolationProductCount: number
  }
  dailyStoreCheck: { pageUpdatedAt: string; totalIssueCount: number }
  logistics: { window: string; abnormalRate: number; abnormalOrderCount: number; falseShipmentExemptCount: number; refundReasonLogisticsExemptCount: number }
  fundRestriction: { recordCount: number; reasons: string[]; amount: number | null; amountNote: string }
  promotionStatus: {
    livePromotion: { status: string; targetRoi: number; budget: string }
    starMall: { status: string; brandKeyword: string; cpmBid: number; budget: string }
  }
  marketingOpportunities: PddMarketingOpportunity[]
  additionalPages: Array<{ pageId: string; name: string; url: string; status: string }>
  daily: PddDailyRecord[]
  products: PddProductRecord[]
  sources: Array<{
    platformId: string
    storeId: string
    pageId: string
    file: string
    sha256: string
    queryInterval: { start: string; end: string } | null
    columns: number | null
    headers: string[] | null
    dataRows: number | null
  }>
}

export function pinduoduoQueryWindowLabel(queryStart: string, queryEnd: string) {
  const start = Date.parse(`${queryStart}T00:00:00Z`)
  const end = Date.parse(`${queryEnd}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '查询期'
  return `${Math.round((end - start) / 86_400_000) + 1}天`
}
